import type { JSX } from 'react';
import React from 'react';
import Link from 'next/link';
import Card from 'ui/src/components/Card';
import { twMerge } from 'utilities/src/twMerge';
import { getCuttedString } from 'utils/textUtils';
import ButtonLink from 'ui/src/components/ButtonLink';

import CardAuthor from './CardAuthor';
import styles from './BlogCard.module.scss';
import Image from '../Image';

export type BlogCardProps = {
  post: PostType;
  withSummary?: boolean;
  withCategories?: boolean;
  imageSizes?: string;
  withAuthor?: boolean;
  withAuthorAvatar?: boolean;
  CustomHeadingTag?: keyof Pick<JSX.IntrinsicElements, 'h1' | 'h2' | 'h3'>;
  isFullImage?: boolean;
  isFirst?: boolean;
  withCardShadow?: boolean;
};

export type PostType = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  timeToRead?: number;
  hideAuthor: boolean;
  category: {
    name: string;
    slug: string;
    databaseId: number;
  };
  featuredImage: {
    file: string;
    altText: string;
  };
  author: {
    image: string;
    name: string;
  };
  lastPublished: string;
};

function BlogCard({
  post,
  withSummary,
  withCategories,
  imageSizes,
  withAuthor,
  withAuthorAvatar = true,
  CustomHeadingTag,
  isFullImage = false,
  isFirst = false,
  withCardShadow = true,
}: BlogCardProps) {
  const categoryName = post.category?.name ?? '';
  const cuttedPostTitle = getCuttedString({ text: post?.title || '', maxNumberOfWords: 3 });

  return (
    <Card
      classes={twMerge('flex flex-col h-full', { 'bg-transparent': isFullImage, 'shadow-lg': withCardShadow })}
    >
      <Card.Image classes="group peer">
        {post.featuredImage ? (
          <Link
            href={`/${post.slug}/`}
            aria-label={post.title ?? ''}
            className={twMerge('relative block', isFullImage ? 'h-60 md:h-44' : 'aspect-video')}
            prefetch={false}
          >
            <Image
              src={post.featuredImage.file}
              sizes={imageSizes}
              alt={post.featuredImage.altText || `${categoryName} - ${cuttedPostTitle} `}
              title={post.featuredImage.altText || 'Featured image'}
              fill
              className={twMerge('object-cover', { 'brightness-50': isFullImage })}
            />
            {isFullImage && (
              <span
                className={twMerge(
                  'text-site-neutrals-0 site-paragraph-xl mdS:max-w-[15.625rem] absolute bottom-3 w-full max-w-[8rem] font-bold group-hover:underline',
                  isFirst ? 'left-3 text-left' : 'right-3 text-right',
                )}
              >
                {post.title}
              </span>
            )}
          </Link>
        ) : (
          <span />
        )}
      </Card.Image>
      {!isFullImage && (
        <Card.Container classes="h-full px-6">
          {withCategories && (
            <Card.Category key={post.category?.databaseId}>
              <Link href={post.category?.slug || '/'} prefetch={false}>
                {post.category?.name}
              </Link>
            </Card.Category>
          )}
          <Card.Title
            wrapperClasses={twMerge({ 'mb-2': withSummary }, { 'mb-8': !withSummary })}
            CustomHeadingTag={CustomHeadingTag}
            classes="text-site-neutrals-600 site-paragraph-xl font-bold"
          >
            <Link href={`/${post.slug}/`} className="hover:underline" prefetch={false}>
              {post.title}
            </Link>
          </Card.Title>
          {withSummary && <Card.Summary classes={styles.clamp}>{post.excerpt}</Card.Summary>}
          <Card.Footer className={!withAuthorAvatar || !withAuthor ? 'h-[66px]' : ''}>
            {withAuthor && (
              <CardAuthor
                withAuthorAvatar={withAuthorAvatar}
                imageUrl={post.author.image}
                name={post.author.name}
              />
            )}
            <Card.MinutesRead
              classes={twMerge({ 'w-full': !withAuthor }, 'text-site-neutrals-300')}
              readingTime={post.timeToRead ?? 0}
            />
          </Card.Footer>
        </Card.Container>
      )}
      {isFullImage && (
        <ButtonLink
          href={`/${post.slug}/`}
          classes={twMerge('peer-hover:border-current', { 'self-end': !isFirst })}
          variant="TextOnly"
          variantColor="Black"
          iconName={isFirst ? 'ArrowBack' : 'ArrowForward'}
          iconPosition={isFirst ? 'left' : 'right'}
          size="Medium"
          prefetch={false}
        >
          {isFirst ? 'Previous article' : 'Next article'}
        </ButtonLink>
      )}
    </Card>
  );
}

export default BlogCard;
